import { useState, useCallback } from 'react';
import { LoginPage } from './pages/LoginPage';
import { PersonaSelectorPage } from './pages/PersonaSelectorPage';
import { ChatPage } from './pages/ChatPage';
import { authLogin, authRegister } from './api/client';
import type { Persona, User } from './types';

type View = 'login' | 'selector' | 'chat';

export default function App() {
  const [view, setView] = useState<View>(() => {
    if (!localStorage.getItem('token')) return 'login';
    if (localStorage.getItem('activePersona')) return 'chat';
    return 'selector';
  });
  const [user,    setUser]    = useState<User | null>(() => {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  });
  const [persona, setPersona] = useState<Persona | null>(() => {
    const raw = localStorage.getItem('activePersona');
    return raw ? JSON.parse(raw) : null;
  });
  const [authErr, setAuthErr] = useState('');
  const [loading, setLoading] = useState(false);

  const login = useCallback(async (username: string, password: string) => {
    setLoading(true);
    setAuthErr('');
    try {
      const data = await authLogin(username, password);
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);
      setView('selector');
      return true;
    } catch (e: any) {
      setAuthErr(e.response?.data?.detail || 'Login failed');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (payload: {
    name: string; email: string; username: string; password: string;
  }) => {
    setLoading(true);
    setAuthErr('');
    try {
      await authRegister(payload);
      return true;
    } catch (e: any) {
      setAuthErr(e.response?.data?.detail || 'Registration failed');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('activePersona');
    setUser(null);
    setPersona(null);
    setView('login');
  }, []);

  const selectPersona = useCallback((p: Persona) => {
    localStorage.setItem('activePersona', JSON.stringify(p));
    setPersona(p);
    setView('chat');
  }, []);

  const switchPersona = useCallback(() => {
    localStorage.removeItem('activePersona');
    setPersona(null);
    setView('selector');
  }, []);

  if (view === 'login') {
    return (
      <LoginPage
        onLogin={login}
        onRegister={register}
        loading={loading}
        error={authErr}
      />
    );
  }

  if (view === 'selector' || !persona) {
    return <PersonaSelectorPage onSelect={selectPersona} />;
  }

  if (!user) {
    return <PersonaSelectorPage onSelect={selectPersona} />;
  }

  return (
    <ChatPage
      persona={persona}
      user={user}
      onSwitchPersona={switchPersona}
      onLogout={logout}
    />
  );
}
